'use client';

import { twMerge } from 'tailwind-merge';
import { useState } from 'react';
import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { useClientHasRendered } from '../../helpers/hooks';
import { useIsMobileScreenWidthOrThinner } from '../../helpers/hooks/useScreenWidth';
import { useGeolocation } from '../../contexts/GeolocationContext/GeolocationContext';
import buffer from '@turf/buffer';
import { point } from '@turf/helpers';
import bbox from '@turf/bbox';
import { turfBoundsToRutilusBounds } from '../../helpers/location';
import { useRutilusQuery } from '../../api/graphql/useRutilusQuery';
import appdownloadPhone from './assets/AppdownloadPhone.png';
import appdownloadPhoneMobile from './assets/AppdownloadPhoneMobile.png';
import { getNearbyFishingWaters } from '../../queries/rutilus/FishingWaters';
import { GetNearbyFishingWaters, GetNearbyFishingWatersVariables } from '../../interfaces/graphql';
import { getNodesFromConnection } from '../../helpers/graphql';
import { trackEvent } from '../../helpers/analytics/trackEvent';
import { Heading } from '../Heading/Heading';
import { Text } from '../Text/Text';
import { Button } from '../Clickables/Buttons/Button';
import { InfoIcon } from '../Icons/InfoIcon';
import { FishingWaterCard } from '../FishingWatersGrid/components/FishingWaterCard';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import { LocationInfoModal } from '../LocationInfoModal/LocationInfoModal';
import { HeadlineLoader } from './components/HeadlineLoader';
import { NearbyWatersBanner } from './components/NearbyWatersBanner';
import { NearbyWatersCTA } from './components/NearbyWatersCTA';
import { CardsLoader } from './components/CardsLoader';

interface IProps {
  className?: string;
  title?: string;
  sourceId?: string;
}

const RADIUS_OF_FISHINGSPOTS_IN_KM = 50; // Radius in kilometers for nearby fishing spots
const MAX_WATERS_TO_SHOW = 6; // Maximum number of waters to show

export const NearbyWaters = ({ className, title = 'Fishing spots near you', sourceId }: IProps) => {
  const { currentPage } = useAnalytics();
  const clientHasRendered = useClientHasRendered();
  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();
  const { isLoading: currentLocationLoading, location } = useGeolocation();

  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [infoModalOpen, setIsInfoModalOpen] = useState(false);

  const latitude = location?.coordinates.latitude;
  const longitude = location?.coordinates.longitude;
  const country = location?.countryCode;
  const region = location?.regionName;

  const buffered =
    latitude && longitude
      ? buffer(point([longitude, latitude]), RADIUS_OF_FISHINGSPOTS_IN_KM, {
          units: 'kilometers',
        })
      : undefined;
  const turfbbox = buffered && bbox(buffered);
  const boundingBox = turfbbox && turfBoundsToRutilusBounds(turfbbox);

  const { data, loading } = useRutilusQuery<
    GetNearbyFishingWaters,
    GetNearbyFishingWatersVariables
  >(getNearbyFishingWaters, {
    variables: { boundingBox, first: MAX_WATERS_TO_SHOW },
    skip: currentLocationLoading,
  });

  const fishingWaters = getNodesFromConnection(data?.entry);

  const fishingWatersCount = fishingWaters.length;
  const isLoading = !clientHasRendered || loading || currentLocationLoading;

  const handleShowMoreClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'NearbyWatersSection',
        source_id: sourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  const handleInfoButtonClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'NearbyWatersSection',
        source_id: sourceId,
        type: 'read-more',
        page: currentPage,
      },
    });
    setIsInfoModalOpen(true);
  };

  return (
    <div data-testid="nearby-waters" className={twMerge('flex flex-col gap-4', className)}>
      {isLoading ? (
        <HeadlineLoader className="flex flex-col sm:mb-4 md:mb-6" />
      ) : (
        <div className="mb-0 sm:mb-4 md:mb-6">
          <Heading className="mb-2" variant="sm" as="h2">
            {title}
          </Heading>
          {region && country && (
            <div className="flex items-center gap-1">
              <Text variant="sm" className="mb-0">
                Result for {region}, {country}
              </Text>
              <Button
                variant="textNone"
                className="body-text-sm flex"
                onClick={handleInfoButtonClick}
              >
                <InfoIcon width={16} height={16} />
              </Button>
            </div>
          )}
        </div>
      )}
      {!isLoading && fishingWaters.length === 0 && <NearbyWatersBanner />}
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-12 sm:gap-4">
        {isLoading ? (
          <CardsLoader />
        ) : (
          fishingWaters.map((fishingWater, index) => (
            <FishingWaterCard
              className={`test-${index} col-span-full sm:col-span-6 md:col-span-4`}
              fishingWater={fishingWater}
              key={fishingWater._id}
            />
          ))
        )}
      </div>
      {isMobileScreenWidthOrThinner && isAppDownloadModalOpen && <NearbyWatersCTA />}
      {fishingWatersCount >= MAX_WATERS_TO_SHOW && !isAppDownloadModalOpen && (
        <Button
          size="sm"
          className="w-full p-2"
          variant="textPrimary"
          onClick={handleShowMoreClick}
        >
          Show more fishing spots
        </Button>
      )}
      <AppDownloadModal
        appDownloadSource="NearbyWatersSection"
        appDownloadSourceId={sourceId}
        modalHeader={'Find prime fishing spots anywhere'}
        modalHeaderVariant="sm"
        modalText="Track catches, find big fish hot spots and check the weekly quality rating of waters."
        isModalOpen={!isMobileScreenWidthOrThinner && isAppDownloadModalOpen}
        image={appdownloadPhone}
        imageMobile={appdownloadPhoneMobile}
        imageWrapperClassName="bg-tuna-tint px-8 sm:p-6 md:mr-4"
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
        textWrapperClassName="mt-6"
        showQRCodeHint
        showTextOnMobile
      />
      <LocationInfoModal
        isModalOpen={infoModalOpen}
        onCloseModal={() => {
          setIsInfoModalOpen(false);
        }}
        radius={RADIUS_OF_FISHINGSPOTS_IN_KM}
      />
    </div>
  );
};
